import type { TareaCompleta } from '@/types/database'

/** Condición fiscal con la que se inscribe a la persona. */
export const CONDICIONES_INSCRIPCION = ['Monotributo', 'Responsable Inscripto', 'Sociedad'] as const
export type CondicionInscripcion = (typeof CONDICIONES_INSCRIPCION)[number]

/** Impuestos del checklist de una inscripción. `rentas_local` lleva además la provincia. */
export const IMPUESTOS_INSCRIPCION = [
  { id: 'monotributo', label: 'Monotributo' },
  { id: 'iva', label: 'IVA' },
  { id: 'ganancias', label: 'Ganancias' },
  { id: 'autonomos', label: 'Autónomos' },
  { id: 'convenio_multilateral', label: 'Convenio Multilateral' },
  { id: 'rentas_local', label: 'Rentas local' },
] as const
export type ImpuestoInscripcion = (typeof IMPUESTOS_INSCRIPCION)[number]['id']

/** Título de la tarea, con el CUIT formateado: «Nueva inscripción · 20-12345678-9». */
export const tituloInscripcion = (cuitFormateado: string) => `Nueva inscripción · ${cuitFormateado}`

/**
 * Títulos de las subtareas: una por impuesto elegido, en el orden del checklist. Rentas local lleva
 * la provincia entre paréntesis.
 */
export const subtareasInscripcion = (
  impuestos: readonly ImpuestoInscripcion[],
  provinciaRentas: string | null,
): string[] =>
  IMPUESTOS_INSCRIPCION.filter(({ id }) => impuestos.includes(id)).map(({ id, label }) =>
    id === 'rentas_local' && provinciaRentas
      ? `Inscripción en ${label} (${provinciaRentas})`
      : `Inscripción en ${label}`,
  )

/** Descripción de la tarea: la condición, la actividad si hay y los comentarios si hay, una por línea. */
export const descripcionInscripcion = ({
  condicion,
  actividad,
  comentarios,
}: {
  condicion: CondicionInscripcion
  actividad: string | null
  comentarios: string | null
}): string =>
  [`Condición: ${condicion}`, actividad?.trim() && `Actividad: ${actividad.trim()}`, comentarios?.trim()]
    .filter(Boolean)
    .join('\n')

/** Ruta del alta de cliente que precarga el CUIT de la inscripción `tareaId`. */
export const rutaAltaDesdeInscripcion = (tareaId: string) =>
  `/clientes?nuevo=true&inscripcion=${encodeURIComponent(tareaId)}`

/** Una inscripción completada cuyo cliente todavía no se dio de alta. */
export const inscripcionSinAlta = (tarea: Pick<TareaCompleta, 'tipo' | 'estado' | 'cliente_id'>) =>
  tarea.tipo === 'inscripcion' && tarea.estado === 'completada' && !tarea.cliente_id

/**
 * Si el usuario puede ver la clave fiscal de una inscripción. Owner, admin y profesional la ven
 * siempre. El asistente la ve si la tarea está asignada a él. Solo lectura nunca. La regla que decide
 * es la de `vault-claves`: esta solo evita ofrecer lo que la function va a rechazar.
 */
export const puedeVerClaveInscripcion = (
  rol: string | null | undefined,
  asignadoA: string | null | undefined,
  email: string | null | undefined,
) =>
  ['owner', 'admin', 'profesional'].includes(rol ?? '') ||
  (rol === 'asistente' && !!email && asignadoA?.toLowerCase() === email.toLowerCase())

/** Organismo, usuario y clave de la clave fiscal de una inscripción, como se cargan en el formulario. */
export interface ClaveFiscalValores {
  organismo: string
  usuario: string
  clave: string
}

export const CLAVE_FISCAL_VACIA: ClaveFiscalValores = { organismo: 'AFIP / ARCA', usuario: '', clave: '' }
